import { useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import Switch from "../../../components/Form/Switch";
import { useTranslationApp } from "../../../lib/i18next";
import {
  AUTOMATIONS_ATTENTION_BANNER,
  AUTOMATIONS_LABEL_FOLDER,
  AUTOMATIONS_LABEL_FREQUENCY,
  AUTOMATIONS_LABEL_NEXT_POST,
  AUTOMATIONS_LABEL_PLATFORMS,
  AUTOMATIONS_MENU_EDIT,
  AUTOMATIONS_MENU_VIEW_CALENDAR,
  AUTOMATIONS_NEXT_NONE,
  AUTOMATIONS_REMAINING_CONTENT,
  AUTOMATIONS_SOURCE_ALL_TITLE,
  AUTOMATIONS_STATUS_ACTIVE,
  AUTOMATIONS_STATUS_ATTENTION,
  AUTOMATIONS_STATUS_ENDED,
  AUTOMATIONS_STATUS_OFF,
  AUTOMATIONS_STATUS_PAUSED,
  AUTOMATIONS_STATUS_SCHEDULING,
  AUTOMATIONS_STATUS_WAITING,
  AUTOMATIONS_WAITING_BANNER,
} from "../../../i18n/keysTranslations";
import PlatformLogo from "../../../components/Common/PlatformLogo";
import {
  AUTOMATION_EFFECTIVE_STATUS,
  isAutomationScheduling,
} from "../../../utils/publishingAutomationStatus";
import { AUTOMATION_SOURCE_ALL } from "../../../components/Modal/ModalCreateAutomation/automationModalConstants";
import { getFrequencyLabelKey } from "../../../utils/publishingAutomationFrequency";

const PAUSED_CHIP_STYLES = {
  color: "#6b7280",
  bgcolor: "#f3f4f6",
  borderColor: "#e5e7eb",
  dotColor: "#9aa1ad",
};

const ATTENTION_CHIP_STYLES = {
  color: "#b45309",
  bgcolor: "#fffbeb",
  borderColor: "#fde68a",
  dotColor: "#d97706",
};

const SCHEDULING_CHIP_STYLES = {
  color: "#1d4ed8",
  bgcolor: "#eff6ff",
  borderColor: "#bfdbfe",
  dotColor: "#2563eb",
};

const DETAILS_SINGLE_COLUMN_MAX = 299;

const getStatusPresentation = ({ effectiveStatus, isScheduling, theme }) => {
  if (isScheduling) {
    return {
      labelKey: AUTOMATIONS_STATUS_SCHEDULING,
      ...SCHEDULING_CHIP_STYLES,
    };
  }

  switch (effectiveStatus) {
    case AUTOMATION_EFFECTIVE_STATUS.ACTIVE:
      return {
        labelKey: AUTOMATIONS_STATUS_ACTIVE,
        color: theme.palette.button.primary.contrastText,
        bgcolor: theme.palette.button.primary.main,
        borderColor: "transparent",
        dotColor: theme.palette.button.primary.contrastText,
      };
    case AUTOMATION_EFFECTIVE_STATUS.WAITING_FOR_CONTENT:
      return {
        labelKey: AUTOMATIONS_STATUS_WAITING,
        ...PAUSED_CHIP_STYLES,
      };
    case AUTOMATION_EFFECTIVE_STATUS.NEEDS_ATTENTION:
      return {
        labelKey: AUTOMATIONS_STATUS_ATTENTION,
        ...ATTENTION_CHIP_STYLES,
      };
    case AUTOMATION_EFFECTIVE_STATUS.OFF:
      return {
        labelKey: AUTOMATIONS_STATUS_OFF,
        ...PAUSED_CHIP_STYLES,
      };
    case AUTOMATION_EFFECTIVE_STATUS.ENDED:
      return {
        labelKey: AUTOMATIONS_STATUS_ENDED,
        ...PAUSED_CHIP_STYLES,
      };
    case AUTOMATION_EFFECTIVE_STATUS.PAUSED:
      return {
        labelKey: AUTOMATIONS_STATUS_PAUSED,
        ...PAUSED_CHIP_STYLES,
      };
    default: {
      const _exhaustive = effectiveStatus;
      void _exhaustive;
      return {
        labelKey: AUTOMATIONS_STATUS_PAUSED,
        ...PAUSED_CHIP_STYLES,
      };
    }
  }
};

const AutomationStatusChip = ({ effectiveStatus, isScheduling = false }) => {
  const theme = useTheme();
  const { t } = useTranslationApp();
  const styles = getStatusPresentation({
    effectiveStatus,
    isScheduling,
    theme,
  });

  return (
    <Chip
      label={
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: styles.dotColor,
              flexShrink: 0,
              display: isScheduling ? "none" : "block",
            }}
          />
          {isScheduling && (
            <CircularProgress
              size={10}
              thickness={5}
              sx={{ color: "inherit" }}
            />
          )}
          {t(styles.labelKey)}
        </Box>
      }
      size="small"
      sx={{
        height: 24,
        maxWidth: "100%",
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1,
        border: "1px solid",
        color: styles.color,
        bgcolor: styles.bgcolor,
        borderColor: styles.borderColor,
        "& .MuiChip-label": {
          px: 1.25,
          py: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  );
};

const AutomationCard = ({
  item,
  canPost,
  onToggleActive,
  onEdit,
  onViewCalendar,
}) => {
  const { t } = useTranslationApp();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const displayName = item.name || "";
  const folderLabel =
    item.sourceType === AUTOMATION_SOURCE_ALL
      ? t(AUTOMATIONS_SOURCE_ALL_TITLE)
      : item.folderLabel || "—";
  const isOff = item.effectiveStatus === AUTOMATION_EFFECTIVE_STATUS.OFF;
  const canOpenEdit = canPost && !isOff;
  const isScheduling = isAutomationScheduling({
    effectiveStatus: item.effectiveStatus,
    desiredStatus: item.desiredStatus,
    queuedCount: item.queuedCount,
    nextPostLabel: item.nextPostLabel,
    lastFillAt: item.lastFillAt,
  });

  const onRowClick = () => {
    if (!canOpenEdit) return;
    onEdit(item);
  };

  return (
    <Box
      onClick={onRowClick}
      sx={{
        border: "1px solid #e4e8ed",
        borderRadius: "14px",
        backgroundColor: "#fff",
        display: "flex",
        alignItems: "stretch",
        gap: 2,
        px: { xs: 1.75, sm: 2.75 },
        py: 2.5,
        minHeight: 102,
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        opacity: isOff ? 0.72 : 1,
        cursor: canOpenEdit ? "pointer" : "default",
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1, containerType: "inline-size" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            mb: 1.5,
            flexWrap: "wrap",
            minWidth: 0,
            pr: { xs: 10, lg: 0 },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              minWidth: 0,
              maxWidth: "100%",
              fontSize: { xs: 16, sm: 18 },
              fontWeight: 600,
              overflowWrap: "anywhere",
            }}
          >
            {displayName}
          </Typography>
          <AutomationStatusChip
            effectiveStatus={item.effectiveStatus}
            isScheduling={isScheduling}
          />
        </Box>

        {item.effectiveStatus ===
          AUTOMATION_EFFECTIVE_STATUS.WAITING_FOR_CONTENT && (
          <Typography sx={{ color: "#6b7280", fontSize: 13, mb: 1 }}>
            {t(AUTOMATIONS_WAITING_BANNER)}
          </Typography>
        )}
        {item.effectiveStatus ===
          AUTOMATION_EFFECTIVE_STATUS.NEEDS_ATTENTION && (
          <Typography sx={{ color: "#b45309", fontSize: 13, mb: 1 }}>
            {t(AUTOMATIONS_ATTENTION_BANNER)}
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            columnGap: 0,
            rowGap: 1.5,
            minWidth: 0,
            [`@container (max-width: ${DETAILS_SINGLE_COLUMN_MAX}px)`]: {
              gridTemplateColumns: "1fr",
              rowGap: 1,
            },
          }}
        >
          <DetailCell label={t(AUTOMATIONS_LABEL_FOLDER)} index={0}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <FolderOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "text.secondary",
                  flexShrink: 0,
                  display: "block",
                }}
              />
              <Box
                component="span"
                sx={{
                  lineHeight: 1.2,
                  mt: 0.27,
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {folderLabel}
              </Box>
            </Box>
          </DetailCell>
          <DetailCell label={t(AUTOMATIONS_LABEL_PLATFORMS)} index={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              {(item.platforms || []).map((platform) => (
                <PlatformLogo
                  key={platform}
                  platform={platform}
                  width={17}
                  height={17}
                  alt={platform}
                />
              ))}
            </Box>
          </DetailCell>
          <DetailCell label={t(AUTOMATIONS_LABEL_FREQUENCY)} index={2}>
            {item.frequencyKey
              ? t(getFrequencyLabelKey(item.frequencyKey))
              : "—"}
          </DetailCell>
          <DetailCell label={t(AUTOMATIONS_LABEL_NEXT_POST)} index={3}>
            {isScheduling ? (
              <CircularProgress size={14} thickness={5} />
            ) : (
              <>
                {item.nextPostLabel || t(AUTOMATIONS_NEXT_NONE)}
                {item.contentRemaining > 0
                  ? ` · ${t(AUTOMATIONS_REMAINING_CONTENT, {
                      count: item.contentRemaining,
                    })}`
                  : ""}
              </>
            )}
          </DetailCell>
        </Box>
      </Box>

      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          alignSelf: "flex-start",
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          ml: { xs: 0, lg: "auto" },
          position: { xs: "absolute", lg: "static" },
          right: { xs: 12, lg: "auto" },
          top: { xs: 16, lg: "auto" },
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          aria-label="more options"
          onClick={(event) => {
            event.stopPropagation();
            setMenuAnchor(event.currentTarget);
          }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            disabled={!canOpenEdit}
            onClick={() => {
              setMenuAnchor(null);
              onEdit(item);
            }}
          >
            {t(AUTOMATIONS_MENU_EDIT)}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onViewCalendar();
            }}
          >
            {t(AUTOMATIONS_MENU_VIEW_CALENDAR)}
          </MenuItem>
        </Menu>
        <Switch
          hasLabel={false}
          size="small"
          isActive={item.active}
          disabled={!canPost || isOff}
          onChangeIsActive={async (checked) => {
            await onToggleActive(item.id, checked);
          }}
        />
      </Box>
    </Box>
  );
};

const DetailCell = ({ label, children, index = 0 }) => {
  const isOddInPair = index % 2 === 0;
  const stackedCellSx = {
    borderRight: 0,
    pl: 0,
    pr: 0,
  };

  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        borderRight: isOddInPair ? "1px solid #edf0f3" : 0,
        pl: isOddInPair ? 0 : 2,
        pr: isOddInPair ? 2 : 0,
        [`@container (max-width: ${DETAILS_SINGLE_COLUMN_MAX}px)`]: stackedCellSx,
      }}
    >
      <Typography
        component="small"
        sx={{
          display: "block",
          color: "#8c96a6",
          fontSize: 14,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: 14,
          minHeight: 20,
          lineHeight: 1.2,
          minWidth: 0,
          maxWidth: "100%",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AutomationCard;
